// useTodayPriority — given the current Today-screen state, returns
// which single "Now" surface should render. The Now zone shows ONE
// card at a time so a user in withdrawal can scan the screen in <3s
// and know what to do next.
//
// Priority order (Phase C.2 — Letters Forward insertion):
//   1. mat            — MAT/inpatient referral recommendation is active
//   2. hold           — Adaptive guidance suggesting a 2-day hold
//   3. letter         — Phase C.2 §P10.7: a "letter to future you" has
//                       come due. Slotted above the handoff because the
//                       letter is a longer-form, planned-for-this-moment
//                       artifact — past-you specifically chose today.
//                       It outranks handoff (which is bedtime → morning
//                       and ephemeral) but stays below MAT and hold
//                       which are clinical / safety-tier surfaces.
//   4. handoff        — Phase C.1 §P10.5: a 280-char operational note
//                       from yesterday-you to today-you. The quietest
//                       "ready to act on" surface — the user wrote it
//                       themselves and only needs to acknowledge it.
//   5. digest         — Sunday/Monday: the new weekly AI digest (replaces
//                       the three-AI-surfaces tangle from earlier phases)
//   6. pawsTomorrow   — Promoted above checkin: surface tomorrow's PAWS
//                       forecast in the evening even before nudging the
//                       check-in CTA. Free clinical defensibility.
//   7. checkin        — Evening and tonight's check-in not yet done
//   8. nudge          — Fresh morning nudge from /api/ai/checkin (opt-in
//                       only after Phase 27.3 demotes this surface)
//   9. null           — Nothing pressing; Zone B collapses entirely.
//
// Crisis is handled separately (a top-of-screen banner, not a Zone B
// card) because it must always win and never be dismissed by another
// priority. The urge-surf prompt lives in Zone C as an always-available
// tool — surfacing it as a "Now" card would compete with check-in,
// and a user mid-craving will scroll to find it regardless.

export type TodayPriorityKind =
  | 'mat'
  | 'hold'
  | 'letter'
  | 'handoff'
  | 'digest'
  | 'pawsTomorrow'
  | 'checkin'
  | 'nudge'
  | null;

export interface TodayPriorityInput {
  /** MAT/inpatient recommendation engine has flagged this user. */
  matSuggested: boolean;
  /** User has dismissed the MAT card for today. */
  matDismissed: boolean;
  /** Adaptive guidance has produced a hold suggestion. */
  hasAdaptiveHold: boolean;
  /** Today's daily-log has been saved. */
  checkInDone: boolean;
  /** Hour of day (0–23) in user's local time. */
  hour: number;
  /**
   * Day of week, 0=Sunday..6=Saturday. Pre-Phase-B this came from
   * `new Date().getDay()` (host TZ). Phase B §P10.6 prefers the
   * digest window to fire in the USER'S timezone so a 11pm-Saturday
   * Pacific user doesn't see the digest until their actual Sunday;
   * when `userTimezone` is supplied, this field is overridden by the
   * Intl-derived weekday inside the hook.
   */
  weekday: number;
  /** Fresh AI nudge message from /api/ai/checkin, if any. */
  nudgeMessage: string | null;
  /** Nudge endpoint loading or errored — don't claim a "nudge" priority while pending. */
  nudgeReady: boolean;
  /** Phase 21 — tomorrow's PAWS forecast is available (user has ≥5 logs + tomorrow's week known). */
  pawsTomorrowReady?: boolean;
  /** Phase 27.2 — fresh weekly digest from /api/ai/digest, ISO timestamp or null. */
  digestGeneratedAt?: string | null;
  /** User has opted in to the morning-nudge surface (Phase 27.3). */
  nudgeOptIn?: boolean;
  /** Phase C.1 §P10.5 — an unexpired, unconsumed hand-off note is waiting. */
  handoffAvailable?: boolean;
  /** Phase C.2 §P10.7 — a letter the user wrote to themselves has come due. */
  letterDelivered?: boolean;
  /**
   * Phase B §P10.6 — IANA timezone for the user (from settings, with
   * an 'America/New_York' fallback at the call site). When set, the
   * digest window check uses this timezone's weekday instead of the
   * raw `weekday` field.
   */
  userTimezone?: string;
  /**
   * Phase B §P10.6 — honest-day suppression. When true, the resolver
   * returns null where it would have returned 'nudge'. The mat / hold
   * / handoff / digest / checkin / pawsTomorrow branches are UNCHANGED;
   * suppression only applies to the morning-encouragement surface.
   */
  honestDay?: boolean;
}

// Window for the weekly digest to claim Zone B priority: Sunday at any
// hour through Monday end-of-day. After Monday, the digest yields to
// daily priorities and is reachable from Insights "Last week's digest →".
function isDigestWindow(weekday: number): boolean {
  return weekday === 0 || weekday === 1;
}

// Map a short weekday name (Intl 'short' format) to the 0=Sun..6=Sat
// integer the rest of the file uses. Unknown strings fall back to -1
// which never matches isDigestWindow.
const WEEKDAY_BY_SHORT: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function weekdayInTz(timezone: string): number {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short' });
    const name = fmt.format(new Date());
    const mapped = WEEKDAY_BY_SHORT[name];
    return mapped == null ? -1 : mapped;
  } catch {
    // Bad TZ string falls through — caller's `weekday` field wins.
    return -1;
  }
}

export function useTodayPriority(input: TodayPriorityInput): TodayPriorityKind {
  const {
    matSuggested,
    matDismissed,
    hasAdaptiveHold,
    checkInDone,
    hour,
    weekday,
    nudgeMessage,
    nudgeReady,
    pawsTomorrowReady,
    digestGeneratedAt,
    nudgeOptIn,
    handoffAvailable,
    letterDelivered,
    userTimezone,
    honestDay,
  } = input;

  // Prefer the user's TZ weekday for the digest window, falling back to
  // the caller-provided `weekday` (host-time) when no TZ is supplied or
  // when the Intl lookup fails.
  const tzWeekday = userTimezone ? weekdayInTz(userTimezone) : -1;
  const effectiveWeekday = tzWeekday >= 0 ? tzWeekday : weekday;

  if (matSuggested && !matDismissed) return 'mat';
  if (hasAdaptiveHold) return 'hold';

  // A letter the user wrote to themselves has just come due. Slotted
  // above the bedtime hand-off because the letter is deliberate
  // (past-you chose THIS day, weeks or months ago) and worth the
  // moment of attention before any other lower-tier nudge fires.
  if (letterDelivered) return 'letter';

  // Quiet hand-off from yesterday-you. Slotted above the digest because
  // a single user-authored sentence ("if you wake up craving: water +
  // headphones") is more actionable than a week-old AI summary, and the
  // user only sees it for as long as they chose (12 / 24 / 72 h).
  if (handoffAvailable) return 'handoff';

  // Sunday + Monday: the weekly digest is the primary AI surface.
  // It wins over check-in nudging because Sunday morning is the
  // "reflect on last week" moment, not the "log tonight" moment.
  if (isDigestWindow(effectiveWeekday) && digestGeneratedAt) return 'digest';

  // PAWS-tomorrow now sits ABOVE the check-in CTA. The prediction is
  // the most clinically defensible surface in the app; the check-in
  // is still surfaced as a CTA from inside the PAWS card.
  if (hour >= 18 && pawsTomorrowReady) return 'pawsTomorrow';

  // After 18:00, surface check-in if not yet done.
  if (!checkInDone && hour >= 18) return 'checkin';

  // Morning nudge is opt-in only after Phase 27.3. Default off for
  // new users; existing users keep their behavior through migration.
  // Phase B §P10.6 — suppress on honest days (over today's target):
  // a cheery one-liner lands wrong on a day the user is already
  // working hard not to feel bad about logging.
  if (nudgeOptIn && nudgeReady && nudgeMessage && !honestDay) return 'nudge';

  return null;
}
